import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { services, companyInfo } from '../data/mock';
import { Send, MessageCircle, Shield, Clock, CheckCircle, Sparkles, Brain } from 'lucide-react';
import { toast } from 'sonner';

const QuotePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    phone: '',
    email: '',
    service: '',
    requirement: '',
    budget: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    toast.success('Quote request submitted successfully! We\'ll get back to you within 24 hours.');
    setFormData({
      name: '',
      companyName: '',
      phone: '',
      email: '',
      service: '',
      requirement: '',
      budget: '',
    });
    setIsSubmitting(false);
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-blue-50/50 to-white relative overflow-hidden">
        <div className="absolute inset-0 ai-grid-pattern" />
        <div className="absolute top-20 right-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4 mr-2" />
              Get Quote
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Let's Plan Your <span className="text-blue-600">Growth Together</span>
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              Fill out the form below and our team will get back to you within 24 hours with a customized proposal.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-lg shadow-gray-100/50">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Doe"
                        className="border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <Input
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        placeholder="Your Company"
                        className="border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        type="tel"
                        placeholder="+91 98765 43210"
                        className="border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        type="email"
                        placeholder="john@company.com"
                        className="border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Interested In *
                      </label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) => handleSelectChange('service', value)}
                      >
                        <SelectTrigger className="border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.title}>
                              {service.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estimated Budget (Optional)
                      </label>
                      <Select
                        value={formData.budget}
                        onValueChange={(value) => handleSelectChange('budget', value)}
                      >
                        <SelectTrigger className="border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue placeholder="Select budget range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="under-25k">Under ₹25,000</SelectItem>
                          <SelectItem value="25k-50k">₹25,000 - ₹50,000</SelectItem>
                          <SelectItem value="50k-1l">₹50,000 - ₹1,00,000</SelectItem>
                          <SelectItem value="1l-5l">₹1,00,000 - ₹5,00,000</SelectItem>
                          <SelectItem value="above-5l">Above ₹5,00,000</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Brief Requirement *
                    </label>
                    <Textarea
                      name="requirement"
                      value={formData.requirement}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      placeholder="Tell us about your project and requirements..."
                      className="border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      size="lg"
                      className="bg-blue-600 text-white hover:bg-blue-700 px-8 font-semibold shadow-lg shadow-blue-600/25"
                    >
                      {isSubmitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          Submit Request
                          <Send className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>
                    <a href={companyInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                      <Button
                        type="button"
                        size="lg"
                        variant="outline"
                        className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 font-semibold w-full sm:w-auto"
                      >
                        <MessageCircle className="mr-2 w-4 h-4" />
                        Quick Chat
                      </Button>
                    </a>
                  </div>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Trust badges */}
              {[
                { icon: Clock, title: 'Quick Response', desc: 'We respond within 24 hours', color: 'blue' },
                { icon: Shield, title: 'Privacy Assured', desc: 'Your information is safe with us', color: 'green' },
                { icon: CheckCircle, title: 'No Obligation', desc: 'Free consultation, no strings attached', color: 'purple' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-6 bg-white border border-gray-100 rounded-xl shadow-sm"
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 rounded-xl bg-${item.color}-50 flex items-center justify-center flex-shrink-0`}>
                      <item.icon className={`w-6 h-6 text-${item.color}-600`} />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{item.title}</h3>
                      <p className="text-gray-500 text-sm">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}

              {/* AI Badge */}
              <div className="p-6 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl text-white">
                <div className="flex items-center gap-3 mb-3">
                  <Brain className="w-8 h-8" />
                  <span className="font-semibold">AI-Powered Solutions</span>
                </div>
                <p className="text-blue-100 text-sm">
                  Our AI technology helps deliver faster results with data-driven insights.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default QuotePage;
